import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';

import cn from 'classnames';

import { Icon, Spinner } from 'components';
import { ButtonV3 } from 'components/ComponentV2';
import { CompanyCard } from 'components/ComponentV2/CompanyCard/CompanyCard.component';
import { ActionMenuButton } from 'components/ActionMenuButton/ActionMenuButton.component';
import { Slider } from 'components/ComponentV2/Slider/Slider.component';
import { CompanyAttributes } from 'lib/models/company';
import { parseResponse } from 'lib/utils/parser';
import { TagAttributes, TagType } from 'lib/models/tag';
import { PAGE_ROUTES } from 'lib/page-routes';
import {
  SponsorCompanyAttributes,
  SponsorshipInitiatorEnum,
  SponsorshipInvitationStateEnum,
  SponsorshipKindEnum,
} from 'lib/models/sponsor-companies';
import { useSponsorCompanies } from 'lib/providers/SocietySponsorshipProvider';
import { RenderCompaniesDataCards } from '../ExploreCompanies/RenderCompaniesDataCards';
import { useSponsorshipPlans } from '../useSponsorshipPlans';
import { SponsorUpdatePlanDataType } from '../Plans/SelectPlanModal/SelectPlanModal.component';
import { NotifySponsorBanner } from '../NotifySponsorBanner/NotifySponsorBanner.component';
import { Header } from './Header/Header.component';

import styles from './Sponsorships.module.scss';

interface SponsorshipsProps {
  viewAllCompanies: () => void;
  viewPlans: () => void;
  copyOffersLink: () => void;
  copyInvitationLink: () => void;
  onMessageCompany: (company: CompanyAttributes) => void;
  onInviteSponsor: (company: CompanyAttributes) => void;
  onAddSponsor: (company: CompanyAttributes) => void;
  onSelectPlan: (data?: SponsorUpdatePlanDataType) => void;
  onRemoveSponsor: (companyId: string, isInConversation: boolean) => void;
}

export const Sponsorships: React.FC<SponsorshipsProps> = (
  props: SponsorshipsProps
) => {
  const {
    viewAllCompanies,
    viewPlans,
    copyOffersLink,
    copyInvitationLink,
    onMessageCompany,
    onAddSponsor,
    onSelectPlan,
    onInviteSponsor,
    onRemoveSponsor,
  } = props;
  const {
    isSponsorsDataLoading,
    inConversationCompanies,
    societySponsorCompanies,
  } = useSponsorCompanies();
  const { sponsorshipPlans } = useSponsorshipPlans();
  const router = useRouter();

  const [messagedCompanyData, setMessagedCompanyData] = useState<
    SponsorCompanyAttributes[]
  >([]);
  const [sponsorCompanyData, setSponsorCompanyData] = useState<
    SponsorCompanyAttributes[]
  >([]);
  const [notifyData, setNotifyData] = useState<SponsorCompanyAttributes[]>([]);
  const [sponsorTranslate, setSponsorTranslate] = useState<number>(0);
  const [inConversationTranslate, setInConversationTranslate] =
    useState<number>(0);

  useEffect(() => {
    const messagedData: SponsorCompanyAttributes[] = [];
    const sponsorData: SponsorCompanyAttributes[] = [];
    const notificationData: SponsorCompanyAttributes[] = [];
    if (societySponsorCompanies && societySponsorCompanies.length) {
      sponsorData.push(...societySponsorCompanies);
    }
    if (inConversationCompanies && inConversationCompanies.length) {
      inConversationCompanies.forEach((data) => {
        if (data.kind === SponsorshipKindEnum.conversation) {
          messagedData.push(data);
        } else if (
          data.kind === SponsorshipKindEnum.request &&
          data.initiator === SponsorshipInitiatorEnum.StudentSociety &&
          data.state !== SponsorshipInvitationStateEnum.rejected
        ) {
          sponsorData.push(data);
        } else if (
          data.kind === SponsorshipKindEnum.request ||
          data.kind === SponsorshipKindEnum.invitor_onboarded
        ) {
          notificationData.push(data);
        }
      });
    }
    setSponsorCompanyData(sponsorData);
    setMessagedCompanyData(messagedData);
    setNotifyData(notificationData);
  }, [inConversationCompanies, societySponsorCompanies]);

  const addSponsor = (company: CompanyAttributes) => {
    if (sponsorshipPlans && sponsorshipPlans.length) {
      onAddSponsor(company);
    } else {
      onSelectPlan();
    }
  };

  const getInConversationActionOptions = (company: CompanyAttributes) => [
    {
      id: '1',
      label: 'Message',
      onClick: () => onMessageCompany(company),
    },
    {
      id: '2',
      label: 'Add as sponsor',
      onClick: () =>
        company.is_verified ? addSponsor(company) : onInviteSponsor(company),
    },
    {
      id: '3',
      label: 'Remove',
      danger: true,
      onClick: () => onRemoveSponsor(company.id, true),
    },
  ];

  const getSponsorActionOptions = (
    company: CompanyAttributes,
    sponsorId: string,
    inConversation: boolean,
    planId?: string,
    huzzleFeatures?: string[],
    customFeatures?: string[]
  ) => [
    {
      id: '1',
      label: 'Change plan',
      onClick: () =>
        onSelectPlan({
          currentPlanId: planId,
          displayCustomFeatures: false,
          sponsorshipId: sponsorId,
          inConversation,
          huzzleFeatures,
          customFeatures,
        }),
    },
    // {
    //   id: '2',
    //   label: 'Customise features',
    //   onClick: () =>
    //     onSelectPlan({
    //       currentPlanId: planId,
    //       displayCustomFeatures: true,
    //       sponsorshipId: sponsorId,
    //       inConversation
    //     }),
    // },
    {
      id: '3',
      label: 'Message',
      onClick: () => onMessageCompany(company),
    },
    {
      id: '4',
      label: inConversation ? 'Cancel request' : 'Remove',
      danger: true,
      onClick: () =>
        onRemoveSponsor(
          inConversation ? company.id : sponsorId,
          inConversation
        ),
    },
  ];

  const getNotificationType = (data: SponsorCompanyAttributes) => {
    switch (data.kind) {
      case SponsorshipKindEnum.invitor_onboarded:
        return 'invitor_onboarded';
      case SponsorshipKindEnum.request:
        if (data.state === SponsorshipInvitationStateEnum.rejected) {
          return 'request_declined';
        } else {
          return 'request_company';
        }
      default:
        return 'invitor_onboarded';
    }
  };

  const onNavigateSponsorCompanies = (translateX: number) => {
    setSponsorTranslate(translateX);
  };

  const onNavigateInConvCompanies = (translateX: number) => {
    setInConversationTranslate(translateX);
  };

  const renderEmptyContent = useMemo(
    () => (
      <div className={styles.SponsorshipsContentEmpty}>
        <div className={styles.info}>
          <Icon iconName="icon_info" className={styles.infoIcon} />
          <p>{"You don't have any sponsors right now."}</p>
          <p>
            Invite companies to sponsor your society or add your existing
            sponsors.
          </p>
        </div>
        <ButtonV3 color="secondary" onClick={viewAllCompanies}>
          Add sponsors
        </ButtonV3>
      </div>
    ),
    [viewAllCompanies]
  );

  const renderCompaniesSlider = () => {
    return (
      <div className={styles.CompaniesSlider}>
        <div className={styles.CompaniesSliderHeader}>
          <p>Companies for you</p>
          <ButtonV3
            size="small"
            variant="text"
            className={styles.viewAllAction}
            onClick={viewAllCompanies}
          >
            View all
          </ButtonV3>
        </div>
        <RenderCompaniesDataCards
          onMessageCompany={onMessageCompany}
          onAddSponsor={onAddSponsor}
          onSelectPlan={onSelectPlan}
          onInviteSponsor={onInviteSponsor}
          displaySlideshow
          sliderClassName={styles.sponsorsSlider}
          navigationClassName={styles.navigationButton}
        />
      </div>
    );
  };

  const renderInConversationCompanies = useMemo(
    () => (
      <div className={styles.sponsorCompanies}>
        <p className={styles.title}>In conversation with</p>
        <Slider scrollWidth={270} onNavigate={onNavigateInConvCompanies}>
          {messagedCompanyData.map((convCompany) => {
            const companyData = parseResponse(convCompany.company);
            const {
              id,
              icon_url,
              name,
              industry: industryResponse,
              huzzle_verified,
              is_verified,
              slug,
            } = companyData;
            const industry =
              parseResponse<TagAttributes<TagType.Industry>>(industryResponse);

            return (
              <CompanyCard
                key={id}
                icon={icon_url}
                name={name}
                title={name}
                caption={industry?.name}
                isHuzzleVerified={huzzle_verified}
                isVerified={is_verified}
                onSelect={() => router.push(`${PAGE_ROUTES.COMPANIES}/${slug}`)}
              >
                <ActionMenuButton
                  className={styles.sliderAction}
                  options={getInConversationActionOptions(companyData)}
                  buttonText="Continue"
                  buttonColor="secondary"
                  buttonSize="small"
                  actionMenuStyle={{
                    transform: `translateX(-${inConversationTranslate}px)`,
                    marginLeft: '35px',
                  }}
                />
              </CompanyCard>
            );
          })}
        </Slider>
      </div>
    ),
    [messagedCompanyData, inConversationTranslate]
  );

  const renderSponsorCompanies = useMemo(
    () => (
      <div className={styles.sponsorCompanies}>
        <Slider scrollWidth={270} onNavigate={onNavigateSponsorCompanies}>
          {sponsorCompanyData.map((sponsor) => {
            const companyData = parseResponse(sponsor.company);
            const {
              id,
              icon_url,
              name,
              industry: industryResponse,
              huzzle_verified,
              is_verified,
              slug,
            } = companyData;
            const industry =
              parseResponse<TagAttributes<TagType.Industry>>(industryResponse);
            const buttonText =
              sponsor.sponsorship_plan?.data?.attributes?.title || 'Custom';
            const inConversation = sponsor.kind === SponsorshipKindEnum.request;
            const planData = sponsor.sponsorship_plan.data;
            const planId = planData?.id;
            const huzzleFeatures = planData?.attributes.custom
              ? planData?.attributes.huzzle_features
              : undefined;
            const customFeatures = planData?.attributes.custom
              ? planData?.attributes.custom_features
              : undefined;

            return (
              <CompanyCard
                key={id}
                icon={icon_url}
                name={name}
                title={name}
                caption={industry?.name}
                isHuzzleVerified={huzzle_verified}
                isVerified={is_verified}
                onSelect={() => router.push(`${PAGE_ROUTES.COMPANIES}/${slug}`)}
                className={cn({ [styles.requestCard]: inConversation })}
              >
                <ActionMenuButton
                  className={styles.sliderAction}
                  options={getSponsorActionOptions(
                    companyData,
                    sponsor.id,
                    inConversation,
                    planId,
                    huzzleFeatures,
                    customFeatures
                  )}
                  buttonText={
                    sponsor.kind === SponsorshipKindEnum.request
                      ? 'Request sent'
                      : buttonText
                  }
                  buttonColor="secondary"
                  buttonSize="small"
                  buttonEndIcon="icon_arrow-down"
                  buttonClassName={cn(styles.sponsorAction, {
                    [styles.requestCardButton]: inConversation,
                  })}
                  actionMenuStyle={{
                    transform: `translateX(-${sponsorTranslate}px)`,
                    marginLeft: '50px',
                  }}
                />
              </CompanyCard>
            );
          })}
        </Slider>
      </div>
    ),
    [sponsorCompanyData, sponsorTranslate]
  );

  const renderNotifications = useMemo(
    () => (
      <div className={styles.notificationData}>
        {notifyData.map((data, index) => (
          <NotifySponsorBanner
            key={index}
            sponsorData={data}
            type={getNotificationType(data)}
            onSelectPlan={() =>
              onSelectPlan({
                currentPlanId: data.sponsorship_plan.data?.id,
                displayCustomFeatures: false,
                sponsorshipId: data.id,
                inConversation: true,
                refreshData: true,
              })
            }
          />
        ))}
      </div>
    ),
    [notifyData]
  );

  return (
    <div className={styles.Sponsorships}>
      <Header
        copyInvitationLink={copyInvitationLink}
        copyOffersLink={copyOffersLink}
        onAddSponsors={viewAllCompanies}
        goToPlans={viewPlans}
      />
      <div className={styles.SponsorshipsContent}>
        {isSponsorsDataLoading ? (
          <Spinner showBackDrop wrapper size="small" />
        ) : (
          <>
            {notifyData.length > 0 && renderNotifications}
            {messagedCompanyData.length === 0 &&
              sponsorCompanyData.length === 0 &&
              renderEmptyContent}
            {sponsorCompanyData.length > 0 && renderSponsorCompanies}
            {messagedCompanyData.length > 0 && renderInConversationCompanies}
            {renderCompaniesSlider()}
          </>
        )}
      </div>
    </div>
  );
};
